// Public accept invite page — /invite/:token (KHÔNG cần auth).
// Style match SignupPage: gradient bg + decorative blobs + logo header + card.
//
// Flow:
//   1. Mount → GET /public/invitations/:token → info (workspace name, user_exists, valid)
//   2. Branch:
//      - !valid → error screen (expired/revoked/exhausted)
//      - targeted + user_exists=false → signup form
//      - targeted + user_exists=true → login form (mật khẩu HOẶC Google)
//      - open link → user tự chọn signup hay login (2-tab)
//   3. Submit:
//      - signup → POST /redeem → set JWT → /inbox
//      - login → POST /accept → set JWT → /select-workspace (highlight tenant mới)
//      - Google → POST /accept-google → như login, khỏi mật khẩu
//
// i18n: trang public chọn ngôn ngữ theo TRÌNH DUYỆT (chưa đăng nhập thì không có locale tài khoản)
// ⇒ Chrome tiếng Anh là ra tiếng Anh. Phải bọc TRỌN trang: bọc lẻ vài câu là người Việt dùng Chrome
// tiếng Anh thấy nhãn Việt lẫn câu Anh trong cùng một form (đo 14/09/2026).

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { Mail, User, Lock, Phone, CheckCircle2, XCircle, LogIn, UserPlus, Loader2, Eye, EyeOff, PartyPopper, Link2 } from 'lucide-react';
import { invitationApi } from '../lib/inboxClient';
import { useAuthStore } from '../lib/authStore';
import { useAuth } from '../contexts/AuthContext';
import LoginChallenge from '@cnv/ui/auth/LoginChallenge';
import GoogleCodeButton, { GOOGLE_CLIENT_ID } from '../components/GoogleCodeButton';
import '../i18n-host';

const REASON_LABELS = {
  expired: msg`Link mời đã hết hạn`,
  revoked: msg`Link mời đã bị huỷ`,
  exhausted: msg`Link mời đã hết lượt sử dụng`,
};

// Nhãn vai trò theo mã `role` — `role_label` BE trả luôn là tiếng Việt (ROLE_LABEL ở
// invitationService), chỉ còn là đường lùi cho mã lạ. `context` vì "Nhân viên"/"Người xem" trùng
// chữ với tiêu đề menu/danh sách (nghĩa số nhiều) — không tách là hai bản dịch đè nhau.
const ROLE_LABELS = {
  tenant_owner: msg({ message: 'Chủ workspace', context: 'vai trò trong lời mời' }),
  tenant_admin: msg({ message: 'Quản trị workspace', context: 'vai trò trong lời mời' }),
  agent: msg({ message: 'Nhân viên', context: 'vai trò trong lời mời' }),
  viewer: msg({ message: 'Người xem', context: 'vai trò trong lời mời' }),
};

// Lỗi BE của trang này → câu cho người đọc, tra theo MÃ.
//
// Dịch ở FE chứ không đọc câu của BE: trang public, không có JWT ⇒ i18nBe không biết ngôn ngữ
// người xem (locale lấy từ claim JWT) — cùng lý do SAAS_ERROR_MAP ở LoginPage. Và TUYỆT ĐỐI không
// hiện `error.message`: đó là chuỗi kỹ thuật. Trước 14/09/2026 trang làm đúng điều đó ⇒ sai mật
// khẩu hiện chữ "unauthorized"; người được mời vào workspace thứ hai (tài khoản tạo từ lời mời
// trước, 3 ngày chưa đăng nhập lại) bấm 4 lần không biết mình sai mật khẩu, cũng không có lối
// đặt lại. Mã là hợp đồng với BE — tests/integration/invitationAccept.test.js giữ.
const ERROR_MAP = {
  invalid_password: msg`Mật khẩu không đúng. Email này đã có tài khoản CNV Work — nhập đúng mật khẩu của tài khoản đó, quên thì bấm «Quên mật khẩu?» để đặt lại.`,
  password_not_set: msg`Tài khoản này chưa có mật khẩu. Bấm «Quên mật khẩu?» để đặt mật khẩu rồi quay lại tham gia.`,
  user_disabled: msg`Tài khoản của bạn đã bị khoá. Liên hệ quản trị viên để mở khoá.`,
  already_member: msg`Bạn đã là thành viên của workspace này — đăng nhập để vào.`,
  user_not_found: msg`Email này chưa có tài khoản — chọn «Tạo tài khoản mới».`,
  user_exists: msg`Email này đã có tài khoản — chuyển sang đăng nhập`,
  phone_taken: msg`Số điện thoại này đã được dùng trong workspace`,
  invitation_not_found: msg`Không tìm thấy lời mời — mở lại đúng link trong email mời.`,
  // Trần đăng nhập theo tài khoản (chung ngân sách với /auth/login). msgid TRÙNG nguyên văn câu BE
  // (middleware/rateLimit.js consumeLoginAttempt) để bản tiếng Việt khớp mọi nơi.
  rate_limited: msg`Thử đăng nhập quá nhiều lần. Vui lòng đợi khoảng một phút rồi thử lại.`,
  invalid_email: msg`Email không đúng định dạng — kiểm tra lại (ví dụ ban@congty.com).`,
  invalid_input: msg`Thông tin nhập chưa hợp lệ — kiểm tra lại các ô rồi thử lại.`,
  // Nhận lời mời bằng Google (POST /accept-google). Hai câu cuối trùng msgid với LoginPage.
  google_email_mismatch: msg`Tài khoản Google này không phải email được mời — chọn đúng tài khoản Google của email trong lời mời.`,
  google_email_not_verified: msg`Email của tài khoản Google này chưa được Google xác minh — dùng mật khẩu hoặc tài khoản Google khác.`,
  google_code_invalid: msg`Không xác minh được tài khoản Google — thử lại.`,
  google_no_email: msg`Tài khoản Google không có email`,
  google_oauth_not_configured: msg`Server chưa cài đặt Google OAuth, báo admin`,
  google_client_secret_missing: msg`Server chưa cài đặt Google OAuth, báo admin`,
};

// Mã để tra ERROR_MAP. Lỗi domain để mã ở `code`; BadInput thì mã thật nằm trong `details.issue`
// (user_not_found, expired…) — riêng lỗi Zod của validate() chỉ có `details.issues[{ path }]`, không
// kèm câu nào (route public không khai userMessage). Ô email là ca hay gặp: trình duyệt coi
// «ban@congty» là hợp lệ, Zod thì không.
function errorKey(err) {
  const e = err?.response?.data?.error;
  if (e?.code !== 'bad_input') return e?.code;
  if (e.details?.issue) return e.details.issue;
  const issues = Array.isArray(e.details?.issues) ? e.details.issues : [];
  if (issues.some((i) => i?.path === 'email')) return 'invalid_email';
  return issues.length ? 'invalid_input' : undefined;
}

// VN phone: 10 chữ số bắt đầu 0, hoặc +84 prefix
function normalizePhone(raw) {
  if (!raw) return '';
  let p = String(raw).replace(/[\s\-.()]/g, '');
  if (p.startsWith('+84')) p = '0' + p.slice(3);
  else if (p.startsWith('84') && p.length >= 11) p = '0' + p.slice(2);
  return p;
}
function isPhoneValid(raw) {
  const p = normalizePhone(raw);
  return /^0\d{9}$/.test(p);
}

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const { t, i18n } = useLingui();
  // Tài khoản cần xác thực 2 lớp / đặt mật khẩu mới: máy chủ ĐÃ cho vào workspace (lời mời đã dùng) rồi trả 403 kèm
  // challenge_token như /auth/login ⇒ hiện bước hai ngay tại đây, xong thì sang màn chọn workspace như thường.
  const auth = useAuth();
  const [joinedAfterChallenge, setJoinedAfterChallenge] = useState(null);
  const takeChallenge = (e) => {
    if (!auth.loginChallenge?.begin(e)) return false;
    const d = e.response.data.error.details;
    setJoinedAfterChallenge(d.joined_tenant_id ? { id: d.joined_tenant_id, name: d.joined_tenant_name } : null);
    return true;
  };

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  // Lỗi form đăng nhập nằm NGAY DƯỚI ô mật khẩu, cạnh link «Quên mật khẩu?». Toast tự tắt sau
  // vài giây — người đang lúng túng chưa đọc kịp đã mất.
  const [loginError, setLoginError] = useState(null);

  // Open link: user tự chọn mode signup hay login (vì BE chưa biết email)
  // Targeted: BE đã biết user_exists → auto branch
  const [openMode, setOpenMode] = useState('signup'); // 'signup' | 'login'

  const [signupForm, setSignupForm] = useState({
    email: '', name: '', password: '', confirmPassword: '', phone: '',
  });
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  useEffect(() => {
    let alive = true;
    invitationApi.getInfo(token).then(
      (data) => {
        if (!alive) return;
        setInfo(data);
        setLoading(false);
        // CHỈ prefill name từ suggested_name khi targeted invite.
        // Open link suggested_name = "mô tả nhóm" (vd "Nhóm Sales batch") không phải tên người.
        if (data && !data.is_open_link && data.suggested_name) {
          setSignupForm((f) => ({ ...f, name: data.suggested_name }));
        }
      },
      (e) => {
        if (!alive) return;
        // Giữ nguyên lỗi, dịch lúc render — effect này không phụ thuộc ngôn ngữ.
        setError(e);
        setLoading(false);
      },
    );
    return () => { alive = false; };
  }, [token]);

  // Câu lỗi theo thứ tự tin cậy: câu FE theo mã → câu BE viết cho người (`data.message`, chỉ có
  // khi lỗi mang userMessage — vd gói hết chỗ) → câu chung truyền vào.
  function describeError(e, fallback) {
    if (!e?.response) return t`Không kết nối được máy chủ — kiểm tra mạng rồi thử lại.`;
    const key = errorKey(e);
    if (ERROR_MAP[key]) return i18n._(ERROR_MAP[key]);
    if (REASON_LABELS[key]) return i18n._(REASON_LABELS[key]);
    const beMsg = e.response.data?.message;
    return typeof beMsg === 'string' && beMsg ? beMsg : fallback;
  }

  // Lỗi của 2 cửa đăng nhập (mật khẩu · Google) cùng hiện ở ô lỗi dưới mật khẩu.
  function showLoginError(e, fallback) {
    const key = errorKey(e);
    // 429 của giới hạn chung (rate_limit_exceeded, CÓ Retry-After): interceptor tự chờ rồi thử lại
    // hoặc tự báo — để nó lo. Trần đăng nhập theo tài khoản (rate_limited) ném từ service nên KHÔNG
    // có Retry-After ⇒ interceptor chỉ biết báo «thử lại sau vài giây», sai (phải chờ tới 1 phút)
    // ⇒ gỡ toast đó, hiện câu đúng ngay dưới ô mật khẩu.
    if (e.response?.status === 429 && key !== 'rate_limited') return;
    if (key === 'rate_limited') toast.dismiss('rate_limited');
    setLoginError(describeError(e, fallback));
  }

  // Người ĐÃ có tài khoản vào workspace xong (mật khẩu hay Google đều trả cùng shape).
  function finishJoin(res) {
    setSession({
      accessToken: res.accessToken,
      refreshToken: res.refreshToken,
      user: res.user,
      active_tenant: res.active_tenant,
    });
    const joinedName = res.joined_tenant_name;
    toast.success(t`Đã tham gia workspace ${joinedName}`);
    navigate('/select-workspace', { replace: true, state: { highlight: res.joined_tenant_id } });
  }

  async function handleSignup(e) {
    e.preventDefault();
    if (signupForm.password.length < 8) {
      toast.error(t`Mật khẩu cần tối thiểu 8 ký tự`);
      return;
    }
    if (signupForm.password !== signupForm.confirmPassword) {
      toast.error(t`Mật khẩu nhập lại không khớp`);
      return;
    }
    if (!isPhoneValid(signupForm.phone)) {
      toast.error(t`Số điện thoại không hợp lệ (10 chữ số, bắt đầu 0)`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await invitationApi.redeem(token, {
        email: info?.is_open_link ? signupForm.email.trim() : null,
        name: signupForm.name.trim(),
        password: signupForm.password,
        phone: normalizePhone(signupForm.phone),
      });
      setSession({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken,
        user: res.user,
        active_tenant: res.active_tenant,
      });
      const workspaceName = res.active_tenant?.display_name || 'workspace';
      toast.success(t`Chào mừng đến ${workspaceName}!`);
      navigate('/inbox', { replace: true });
    } catch (e) {
      if (takeChallenge(e)) return;
      // 429: interceptor đã tự báo "Thao tác quá nhanh" — báo thêm ở đây thành 2 thông báo.
      if (e.response?.status !== 429) toast.error(describeError(e, t`Đăng ký thất bại`));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setSubmitting(true);
    setLoginError(null);
    try {
      const res = await invitationApi.accept(token, {
        email: info?.is_open_link ? loginForm.email.trim() : null,
        password: loginForm.password,
      });
      finishJoin(res);
    } catch (e) {
      if (takeChallenge(e)) return;
      showLoginError(e, t`Đăng nhập thất bại — thử lại sau ít phút.`);
    } finally {
      setSubmitting(false);
    }
  }

  // Nút Google tự quản vòng quay của nó (GoogleCodeButton) — ở đây chỉ đổi code lấy phiên.
  async function handleGoogleCode(code) {
    setLoginError(null);
    try {
      const res = await invitationApi.acceptWithGoogle(token, { code });
      finishJoin(res);
    } catch (e) {
      if (takeChallenge(e)) return;
      showLoginError(e, t`Đăng nhập Google thất bại — thử lại`);
    }
  }

  if (loading) {
    return (
      <PageShell>
        <div className="text-center text-slate-500 py-10 flex items-center justify-center gap-2">
          <Loader2 size={18} className="animate-spin" /> <Trans>Đang kiểm tra lời mời...</Trans>
        </div>
      </PageShell>
    );
  }

  if (auth.loginChallenge?.pending) {
    return (
      <PageShell>
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6">
          {joinedAfterChallenge?.name && (
            <p className="text-sm text-emerald-700 mb-4"><Trans>Đã tham gia workspace {joinedAfterChallenge.name}.</Trans></p>
          )}
          <LoginChallenge
            lc={auth.loginChallenge}
            onSuccess={() => navigate('/select-workspace', {
              replace: true, state: joinedAfterChallenge?.id ? { highlight: joinedAfterChallenge.id } : undefined,
            })}
          />
        </div>
      </PageShell>
    );
  }

  if (error || !info) {
    const notFound = t`Không tìm thấy lời mời`;
    return (
      <PageShell>
        <ErrorCard title={t`Link không hợp lệ`} message={error ? describeError(error, notFound) : notFound} />
      </PageShell>
    );
  }

  if (!info.valid) {
    return (
      <PageShell>
        <ErrorCard
          title={REASON_LABELS[info.reason] ? i18n._(REASON_LABELS[info.reason]) : t`Link không còn hiệu lực`}
          message={t`Liên hệ admin của workspace để xin link mới.`}
        />
      </PageShell>
    );
  }

  const clearLoginError = () => setLoginError(null);
  const tenantName = info.tenant_name;
  const inviterName = info.inviter_name;
  const inviteEmail = info.email;
  const roleLabel = ROLE_LABELS[info.role] ? i18n._(ROLE_LABELS[info.role]) : info.role_label;

  return (
    <PageShell>
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
        {/* Header tenant + role */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto mb-3 bg-pink-50 rounded-2xl flex items-center justify-center">
            <PartyPopper className="w-7 h-7 text-[#A8133D]" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900">
            <Trans>Tham gia <span className="text-[#A8133D]">{tenantName}</span></Trans>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {inviterName
              ? <Trans><strong>{inviterName}</strong> đã mời bạn với vai trò <strong>{roleLabel}</strong></Trans>
              : <Trans>Bạn được mời với vai trò <strong>{roleLabel}</strong></Trans>}
          </p>
          {inviteEmail ? (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs">
              <CheckCircle2 className="w-3.5 h-3.5" /> <Trans>Email <strong>{inviteEmail}</strong> đã xác thực</Trans>
            </div>
          ) : (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-xs">
              <Link2 className="w-3.5 h-3.5" /> <Trans>Link công khai — bạn tự nhập email</Trans>
            </div>
          )}
        </div>

        {info.is_open_link ? (
          <>
            <div className="grid grid-cols-2 gap-2 mb-5">
              <button type="button" onClick={() => setOpenMode('signup')}
                className={`p-2 text-sm font-medium rounded-md transition ${
                  openMode === 'signup' ? 'bg-blue-100 text-blue-700' : 'text-slate-600 hover:bg-slate-100'
                }`}>
                <UserPlus className="w-4 h-4 inline mr-1" /> <Trans>Tạo tài khoản mới</Trans>
              </button>
              <button type="button" onClick={() => setOpenMode('login')}
                className={`p-2 text-sm font-medium rounded-md transition ${
                  openMode === 'login' ? 'bg-blue-100 text-blue-700' : 'text-slate-600 hover:bg-slate-100'
                }`}>
                <LogIn className="w-4 h-4 inline mr-1" /> <Trans>Đã có tài khoản</Trans>
              </button>
            </div>
            {openMode === 'signup' ? (
              <NewUserForm info={info} form={signupForm} setForm={setSignupForm}
                onSubmit={handleSignup} submitting={submitting} showPwd={showPwd} setShowPwd={setShowPwd} />
            ) : (
              <ExistingUserForm info={info} form={loginForm} setForm={setLoginForm}
                onSubmit={handleLogin} submitting={submitting}
                error={loginError} onClearError={clearLoginError} onGoogleCode={handleGoogleCode} />
            )}
          </>
        ) : info.user_exists ? (
          <ExistingUserForm info={info} form={loginForm} setForm={setLoginForm}
            onSubmit={handleLogin} submitting={submitting}
            error={loginError} onClearError={clearLoginError} onGoogleCode={handleGoogleCode} />
        ) : (
          <NewUserForm info={info} form={signupForm} setForm={setSignupForm}
            onSubmit={handleSignup} submitting={submitting} showPwd={showPwd} setShowPwd={setShowPwd} />
        )}
      </div>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-purple-50 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-pink-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-rose-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-6">
          <img src="/cnvwork.png" alt="CNV Work" className="w-20 h-20 mx-auto rounded-2xl shadow-lg mb-4 object-contain" />
          <h1 className="text-2xl font-bold text-slate-900">CNV Work</h1>
          <p className="text-xs font-semibold text-slate-500 mt-1 tracking-widest">
            CRM · OPERATIONS · AI AGENT
          </p>
        </div>
        {children}
      </div>
    </div>
  );
}

function NewUserForm({ info, form, setForm, onSubmit, submitting, showPwd, setShowPwd }) {
  const { t } = useLingui();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5"><Trans>Email</Trans></label>
        <div className="relative">
          <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          {info.is_open_link ? (
            <input
              type="email" required autoFocus value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder={t`ban@congty.com`}
              className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          ) : (
            <input
              type="email" readOnly value={info.email}
              className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 text-slate-600 cursor-not-allowed text-sm"
            />
          )}
        </div>
      </div>

      {/* Họ tên */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          <Trans>Họ tên</Trans> <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text" required value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder={t`Nguyễn Văn A`}
            className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
      </div>

      {/* Số điện thoại — bắt buộc, validate VN */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          <Trans>Số điện thoại</Trans> <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="tel" required value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            placeholder="0912 345 678"
            className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
        {form.phone && !isPhoneValid(form.phone) && (
          <p className="text-xs text-red-600 mt-1"><Trans>Số điện thoại không hợp lệ (10 chữ số, bắt đầu 0)</Trans></p>
        )}
      </div>

      {/* Mật khẩu */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          <Trans>Mật khẩu</Trans> <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type={showPwd ? 'text' : 'password'} required minLength={8} value={form.password}
            onChange={(e) => set('password', e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
          <button type="button" onClick={() => setShowPwd((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-1"><Trans>Tối thiểu 8 ký tự</Trans></p>
      </div>

      {/* Nhập lại mật khẩu */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          <Trans>Nhập lại mật khẩu</Trans> <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type={showPwd ? 'text' : 'password'} required value={form.confirmPassword}
            onChange={(e) => set('confirmPassword', e.target.value)}
            className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
        {form.confirmPassword && form.password !== form.confirmPassword && (
          <p className="text-xs text-red-600 mt-1"><Trans>Mật khẩu nhập lại không khớp</Trans></p>
        )}
      </div>

      <button type="submit" disabled={submitting}
        className="w-full py-2.5 bg-[#A8133D] hover:bg-[#7A0D2C] text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition disabled:opacity-60">
        {submitting && <Loader2 size={16} className="animate-spin" />}
        {submitting ? t`Đang xử lý...` : t`Tham gia workspace`}
      </button>
    </form>
  );
}

function ExistingUserForm({ info, form, setForm, onSubmit, submitting, error, onClearError, onGoogleCode }) {
  const { t } = useLingui();
  const inviteEmail = info.email;
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {GOOGLE_CLIENT_ID && (
        <div>
          <GoogleCodeButton onCode={onGoogleCode} />
          {/* Lời mời gắn email: BE chỉ nhận đúng tài khoản Google của email đó — nói trước để khỏi chọn nhầm. */}
          {inviteEmail && (
            <p className="text-xs text-slate-500 mt-1.5 text-center">
              <Trans>Chọn tài khoản Google của <strong>{inviteEmail}</strong></Trans>
            </p>
          )}
          <div className="relative mt-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-slate-400"><Trans>hoặc</Trans></span>
            </div>
          </div>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5"><Trans>Email</Trans></label>
        <div className="relative">
          <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          {info.is_open_link ? (
            <input
              type="email" required autoFocus value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder={t`ban@congty.com`}
              className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          ) : (
            <input
              type="email" readOnly value={info.email}
              className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 text-slate-600 cursor-not-allowed text-sm"
            />
          )}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <label className="block text-sm font-medium text-slate-700">
            <Trans>Mật khẩu</Trans> <span className="text-red-500">*</span>
          </label>
          {/* Tab MỚI: trang mời vẫn mở nguyên — đặt lại mật khẩu xong quay về đây gõ mật khẩu mới là
              tham gia luôn, khỏi lục lại email mời. */}
          <a href="/forgot-password" target="_blank" rel="noopener noreferrer"
            className="text-xs font-medium text-[#A8133D] hover:underline">
            <Trans>Quên mật khẩu?</Trans>
          </a>
        </div>
        <div className="relative">
          <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="password" required autoFocus={!info.is_open_link && !GOOGLE_CLIENT_ID} value={form.password}
            onChange={(e) => { setForm((f) => ({ ...f, password: e.target.value })); onClearError(); }}
            aria-invalid={error ? true : undefined}
            className={`w-full pl-10 pr-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
              error ? 'border-red-400' : 'border-slate-300'
            }`}
          />
        </div>
        {/* Người được mời lần thứ hai hay tưởng trang này bắt ĐẶT mật khẩu cho workspace mới. */}
        <p className="text-xs text-slate-500 mt-1">
          <Trans>Nhập mật khẩu tài khoản CNV Work bạn đang dùng, không phải đặt mật khẩu mới.</Trans>
        </p>
      </div>
      {error && (
        <div role="alert" className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <button type="submit" disabled={submitting}
        className="w-full py-2.5 bg-[#A8133D] hover:bg-[#7A0D2C] text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition disabled:opacity-60">
        {submitting && <Loader2 size={16} className="animate-spin" />}
        {submitting ? t`Đang xử lý...` : t`Đăng nhập + Tham gia`}
      </button>
    </form>
  );
}

function ErrorCard({ title, message }) {
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
      <div className="w-16 h-16 mx-auto mb-4 bg-red-50 rounded-2xl flex items-center justify-center">
        <XCircle className="w-8 h-8 text-red-500" />
      </div>
      <h1 className="text-xl font-bold text-slate-900 mb-2">{title}</h1>
      <p className="text-sm text-slate-600">{message}</p>
    </div>
  );
}
